import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { signIn, signUp } from '../lib/auth';
import { PhoneInput } from './PhoneInput';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { supabase } from '../lib/supabase';

interface AuthFormProps {
  onSuccess: () => void;
}

export function AuthForm({ onSuccess }: AuthFormProps) {
  const [isLogin, setIsLogin] = React.useState(true);
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const [name, setName] = React.useState('');
  const [whatsapp, setWhatsapp] = React.useState(() => {
    return localStorage.getItem('rememberedWhatsapp') || '';
  });
  const [password, setPassword] = React.useState('');
  const [referralCode, setReferralCode] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(() => {
    return !!localStorage.getItem('rememberedWhatsapp');
  });
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (isLogin) {
        await signIn(whatsapp, password);
        
        if (rememberMe) {
          localStorage.setItem('rememberedWhatsapp', whatsapp);
        } else {
          localStorage.removeItem('rememberedWhatsapp');
        }
      } else {
        await signUp(whatsapp, password, name, referralCode);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setIsLogin(true);
    setError(null);
    setName('');
    setReferralCode('');
  };

  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div>
      {!isLogin && (
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Voltar ao login</span>
        </button>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        {!isLogin && (
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nome completo
            </label>
            <input
              required
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        <div>
          <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
            WhatsApp
          </label>
          <PhoneInput
            id="whatsapp"
            value={whatsapp}
            onChange={setWhatsapp}
            placeholder="(11) 98765-4321"
            required
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Senha
          </label>
          <input
            required
            type="password"
            id="password"
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          {!isLogin && (
            <p className="mt-1 text-sm text-gray-500">
              Mínimo de 6 caracteres
            </p>
          )}
        </div>

        {!isLogin && (
          <div>
            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1">
              Cupom de indicação (opcional)
            </label>
            <input
              type="text"
              id="referralCode"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="Digite o código se tiver"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              maxLength={10}
            />
          </div>
        )}

        {isLogin && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                Lembrar meu WhatsApp
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              Esqueci minha senha
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
        >
          {isLoading ? 'Processando...' : isLogin ? 'Entrar' : 'Criar Conta'}
        </button>

        {isLogin && (
          <button
            type="button"
            onClick={() => {
              setIsLogin(false);
              setError(null);
            }}
            className="w-full px-4 py-3 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors font-medium"
          >
            Novo Cadastro
          </button>
        )}
      </form>
    </div>
  );
}